// src/pages/VentureDetail.js — one Rainmaker Venture (migrations/090): its
// weekly-doc fields and the people and firms on it (venture_links), each
// with a role. A linked contact or firm shows the venture on its profile
// (VenturesLinked.js).
import { useState } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '../lib/api'
import { EntitySearch } from '../components/EntityPicker'
import ActionItemsList from '../components/ActionItemsList'
import { PeekLink } from '../components/Peek'
import { EventModal } from './Events'
import { VentureForm } from './Ventures'

const errText = e => e?.response?.data?.error || e?.message

function Field({ label, children }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 13, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{children || <span style={{ color: 'var(--text-muted)' }}>—</span>}</div>
    </div>
  )
}

export default function VentureDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [adding, setAdding] = useState(null)   // 'contact' | 'firm' | null
  const [role, setRole] = useState('')
  const [confirmDelete, setConfirmDelete] = useState(false)

  const { data: v, isLoading, error } = useQuery({
    queryKey: ['venture', id],
    queryFn: () => api.get(`/api/ventures/${id}`).then(r => r.data.data),
  })
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['venture', id] })
    qc.invalidateQueries({ queryKey: ['ventures'] })
    qc.invalidateQueries({ queryKey: ['ventures-linked'] })
  }

  const save = useMutation({
    mutationFn: payload => api.patch(`/api/ventures/${id}`, payload),
    onSuccess: () => { refresh(); setEditing(false) },
  })
  const remove = useMutation({
    mutationFn: () => api.delete(`/api/ventures/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ventures'] }); navigate('/ventures') },
  })
  const addLink = useMutation({
    mutationFn: payload => api.post(`/api/ventures/${id}/links`, payload),
    onSuccess: () => { refresh(); setAdding(null); setRole('') },
  })
  const updateLink = useMutation({
    mutationFn: ({ linkId, role }) => api.patch(`/api/ventures/${id}/links/${linkId}`, { role }),
    onSuccess: refresh,
  })
  const removeLink = useMutation({
    mutationFn: linkId => api.delete(`/api/ventures/${id}/links/${linkId}`),
    onSuccess: refresh,
  })

  if (isLoading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><div className="spinner spinner-lg" /></div>
  if (error || !v) {
    return (
      <div className="page">
        <div className="empty-state">
          <h3>Venture not found</h3>
          <Link to="/ventures" className="btn btn-secondary" style={{ marginTop: 12 }}>Back to Rainmaker Ventures</Link>
        </div>
      </div>
    )
  }

  const linkedContacts = new Set(v.links.map(l => l.contact_id).filter(Boolean))
  const linkedFirms = new Set(v.links.map(l => l.firm_id).filter(Boolean))
  // Leads first, then everyone else in the order they were added.
  const isLead = l => /^lead$/i.test(l.role ?? '')
  const keyPlayers = [...v.links.filter(isLead), ...v.links.filter(l => !isLead(l))]

  return (
    <div className="page">
      <div style={{ marginBottom: 12 }}>
        <Link to="/ventures" style={{ fontSize: 13, color: 'var(--text-secondary)' }}>← Rainmaker Ventures</Link>
      </div>

      <div className="page-header">
        <div className="page-header-text">
          <h1>{v.name}</h1>
          <p style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <span className="badge badge-blue">{v.ref}</span>
            {v.owner && <span>Owner: {v.owner.name}</span>}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {confirmDelete ? (
            <>
              <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                Delete this venture? Its contacts and firms are not touched.
              </span>
              <button className="btn btn-secondary" onClick={() => setConfirmDelete(false)}>Cancel</button>
              <button className="btn btn-danger" onClick={() => remove.mutate()} disabled={remove.isPending}>Delete</button>
            </>
          ) : (
            <>
              <button className="btn btn-secondary" onClick={() => setConfirmDelete(true)}>Delete</button>
              <button className="btn btn-primary" onClick={() => setEditing(true)}>Edit</button>
            </>
          )}
        </div>
      </div>

      <div className="card" style={{ marginBottom: 20, display: 'grid', gap: 16 }}>
        <Field label="What it is">{v.description}</Field>
        {v.notes && <Field label="Notes">{v.notes}</Field>}
      </div>

      {/* Action items for this venture, every assignee (as on a mandate) */}
      <div className="card" style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>Action Items</h2>
        <ActionItemsList filter={{ venture_id: v.venture_id }} defaultVentureId={v.venture_id}
          emptyText="No open action items for this venture." />
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
          <h2 style={{ fontSize: 14, fontWeight: 600 }}>Key players</h2>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setAdding('contact')}>+ Person</button>
            <button className="btn btn-secondary btn-sm" onClick={() => setAdding('firm')}>+ Firm</button>
          </div>
        </div>

        {adding && (
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr) auto', gap: 8, alignItems: 'start', marginBottom: 12 }}>
            <div style={{ padding: '6px 10px', background: 'var(--surface-raised)', border: '1px solid var(--border)', borderRadius: 6 }}>
              <EntitySearch kind={adding} exclude={adding === 'contact' ? linkedContacts : linkedFirms}
                placeholder={adding === 'contact' ? 'Find a person' : 'Find a firm'}
                onPick={pickedId => addLink.mutate({ [`${adding}_id`]: pickedId, role: role.trim() || null })}
                onCancel={() => setAdding(null)} />
            </div>
            <input className="input" value={role} onChange={e => setRole(e.target.value)} placeholder="Role, e.g. Lead or Partner" />
            <button className="btn btn-ghost btn-sm" onClick={() => setAdding(null)}>Cancel</button>
          </div>
        )}
        {addLink.error && <div style={{ fontSize: 12, color: 'var(--danger-text)', marginBottom: 8 }}>{errText(addLink.error)}</div>}

        {!v.links.length ? (
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No one linked yet.</div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead><tr><th>Name</th><th>Firm / type</th><th>Role</th><th /></tr></thead>
              <tbody>
                {keyPlayers.map(l => (
                  <tr key={l.link_id}>
                    <td style={{ fontWeight: 500 }}>
                      {l.contact
                        ? <PeekLink type="contact" id={l.contact.contact_id}>{l.contact.name}</PeekLink>
                        : <PeekLink type="firm" id={l.firm?.firm_id}>{l.firm?.name}</PeekLink>}
                    </td>
                    <td style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                      {l.contact ? (l.contact.primary_firm?.name ?? '—') : ([].concat(l.firm?.firm_type ?? []).join(', ') || 'Firm')}
                    </td>
                    <td>
                      <input className="input" defaultValue={l.role ?? ''} style={{ padding: '5px 8px', fontSize: 13, maxWidth: 200 }}
                        onBlur={e => { if ((e.target.value.trim() || null) !== (l.role ?? null)) updateLink.mutate({ linkId: l.link_id, role: e.target.value }) }} />
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => removeLink.mutate(l.link_id)}>Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <EventModal title="Edit Venture" onClose={() => setEditing(false)}>
          <VentureForm initial={v} onSubmit={save.mutate} onCancel={() => setEditing(false)}
            submitting={save.isPending} error={errText(save.error)} />
        </EventModal>
      )}
    </div>
  )
}
